import {
  CaretDownOutlined,
  CaretUpOutlined,
  DownOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Dropdown,
  Input,
  InputNumber,
  Modal,
  Select,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Amount,
  StockLink,
  StockPrice,
  ratioToStockPricePct,
  StockProfitRate,
  type StockDetailData,
  CanvasCurveChart,
  type CurveDataPoint,
} from "fin-web-components";
import { queryStockDetailInfoForLongfans } from "../../components/fin-web/stockDetailQuery";
import { useStockLinkDetail, stockLinkDetailPropsForCode } from "../../components/fin-web/useStockLinkDetail";
import StockNoteImportPicker from "./StockNoteImportPicker";
import type { StockSearchBrief } from "../../global/GlobalType";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import type { Dayjs } from "dayjs";
import { UserHoldingsApi } from "./UserHoldingsApi";
import type {
  AppUserHoldingLineDTO,
  AppUserTradeHistoryLineDTO,
  ClosedTradeLineDTO,
  ProfitCurvePointDTO,
} from "./UserHoldingsType";
import { UserWatchlistApi } from "./UserWatchlistApi";
import type { UserWatchlistGroupDTO, UserWatchlistItemDTO } from "./UserWatchlistType";
import { WatchlistCreateGroupModal } from "./WatchlistCreateGroupModal";
import { AddWatchlistItemModal } from "./AddWatchlistItemModal";
import { OpenBuyModal } from "./OpenBuyModal";
import { userHoldingsPageCssVars } from "./Config";
import "./UserHoldingsPage.css";

dayjs.extend(customParseFormat);

const { Text } = Typography;
const RECT_BTN = "user-holdings-rect-modal-btn";

const WATCHLIST_GROUP_NAV_GAP = 10;

/** 自选分组：横向展示；分组超过 8 个时必显「更多分组」，单行最多 8 个分组按钮 + 更多 */
const WATCHLIST_GROUP_NAV_INLINE_MAX = 8;
/** 自选筛选项「全部」：watchGroupId 空串表示查全部自选 */
const WATCHLIST_GROUP_ALL = "";

function WatchlistGroupsNav(props: {
  groups: UserWatchlistGroupDTO[];
  watchGroupId: string;
  onSelectGroup: (groupId: string) => void;
}): JSX.Element {
  const { groups, watchGroupId, onSelectGroup } = props;
  const growRef = useRef<HTMLDivElement>(null);
  const measureWrapRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(0);
  const [widths, setWidths] = useState<number[]>([]);
  const [moreW, setMoreW] = useState(56);
  const [moreOpen, setMoreOpen] = useState(false);

  const groupKey = useMemo(() => groups.map((g) => String(g.id ?? "")).join("|"), [groups]);
  const allOn = watchGroupId === WATCHLIST_GROUP_ALL;

  useLayoutEffect(() => {
    const el = growRef.current;
    if (!el) {
      return;
    }
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? el.clientWidth;
      setContainerW(w);
    });
    ro.observe(el);
    setContainerW(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    const wrap = measureWrapRef.current;
    if (!wrap || groups.length === 0) {
      setWidths([]);
      return;
    }
    const btns = wrap.querySelectorAll<HTMLElement>("[data-measure-group]");
    const arr = Array.from(btns).map((b) => b.offsetWidth);
    setWidths(arr);
  }, [groupKey, groups]);

  const layout = useMemo(() => {
    const n = groups.length;
    const gap = WATCHLIST_GROUP_NAV_GAP;
    if (n === 0 || widths.length !== n) {
      return { mode: "all" as const, k: n };
    }
    if (containerW <= 0) {
      if (n > WATCHLIST_GROUP_NAV_INLINE_MAX) {
        return {
          mode: "trunc" as const,
          k: Math.min(WATCHLIST_GROUP_NAV_INLINE_MAX, n - 1),
        };
      }
      return { mode: "all" as const, k: n };
    }
    const sumAll = widths.reduce((a, b) => a + b, 0) + Math.max(0, n - 1) * gap;
    if (n <= WATCHLIST_GROUP_NAV_INLINE_MAX && sumAll <= containerW) {
      return { mode: "all" as const, k: n };
    }
    const maxK =
      n > WATCHLIST_GROUP_NAV_INLINE_MAX
        ? Math.min(WATCHLIST_GROUP_NAV_INLINE_MAX, n - 1)
        : n - 1;
    for (let k = maxK; k >= 0; k--) {
      const prefix = k === 0 ? 0 : widths.slice(0, k).reduce((a, b) => a + b, 0);
      const gaps = k === 0 ? 0 : k * gap;
      const total = prefix + gaps + moreW;
      if (total <= containerW) {
        return { mode: "trunc" as const, k };
      }
    }
    return { mode: "trunc" as const, k: 0 };
  }, [groups.length, widths, containerW, moreW]);

  const selectedIndex = useMemo(() => {
    if (allOn) {
      return -1;
    }
    const i = groups.findIndex((g) => String(g.id ?? "") === watchGroupId);
    return i >= 0 ? i : -1;
  }, [groups, watchGroupId, allOn]);

  const moreTriggerLabel = useMemo(() => {
    if (layout.mode !== "trunc") {
      return "更多分组";
    }
    if (selectedIndex < 0) {
      return "更多分组";
    }
    if (selectedIndex >= layout.k) {
      return String(groups[selectedIndex]?.groupName ?? "—");
    }
    return "更多分组";
  }, [layout.mode, layout.k, selectedIndex, groups]);

  useLayoutEffect(() => {
    const wrap = measureWrapRef.current;
    if (!wrap || groups.length === 0) {
      return;
    }
    const m = wrap.querySelector<HTMLElement>("[data-measure-more]");
    setMoreW(m?.offsetWidth ?? 56);
  }, [groupKey, groups, moreTriggerLabel]);

  const moreActive = layout.mode === "trunc" && selectedIndex >= layout.k;

  const allBtn = (
    <button
      type="button"
      className={
        allOn
          ? "uh-watchlist-group-text-btn uh-watchlist-group-text-btn--on"
          : "uh-watchlist-group-text-btn"
      }
      onClick={() => {
        onSelectGroup(WATCHLIST_GROUP_ALL);
        setMoreOpen(false);
      }}
    >
      全部
    </button>
  );

  if (groups.length === 0) {
    return (
      <div className="uh-watchlist-groups-nav-inner" style={{ display: "flex", gap: WATCHLIST_GROUP_NAV_GAP }}>
        {allBtn}
      </div>
    );
  }

  const renderGroupBtn = (g: UserWatchlistGroupDTO, idx: number) => {
    const gid = String(g.id ?? "");
    const gname = String(g.groupName ?? "—");
    const on = watchGroupId === gid;
    return (
      <button
        key={g.id ?? `g-${idx}`}
        type="button"
        className={
          on
            ? "uh-watchlist-group-text-btn uh-watchlist-group-text-btn--on"
            : "uh-watchlist-group-text-btn"
        }
        onClick={() => {
          onSelectGroup(gid);
          setMoreOpen(false);
        }}
      >
        {gname}
      </button>
    );
  };

  const morePanel = (
    <div className="uh-watchlist-more-panel" role="listbox">
      {groups.map((g, idx) => {
        const gid = String(g.id ?? "");
        const gname = String(g.groupName ?? "—");
        const on = watchGroupId === gid;
        return (
          <button
            key={g.id ?? `m-${idx}`}
            type="button"
            role="option"
            className={
              on ? "uh-watchlist-more-cell uh-watchlist-more-cell--on" : "uh-watchlist-more-cell"
            }
            onClick={() => {
              onSelectGroup(gid);
              setMoreOpen(false);
            }}
            title={gname}
          >
            <span className="uh-watchlist-more-cell-text">{gname}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="uh-watchlist-groups-nav-root">
      <div ref={growRef} className="uh-watchlist-groups-inline">
        {allBtn}
        {layout.mode === "all" && groups.map((g, i) => renderGroupBtn(g, i))}
        {layout.mode === "trunc" && (
          <>
            {groups.slice(0, layout.k).map((g, i) => renderGroupBtn(g, i))}
            <Dropdown
              trigger={["click"]}
              open={moreOpen}
              onOpenChange={setMoreOpen}
              popupRender={() => morePanel}
              overlayStyle={{ padding: 0, background: "transparent", boxShadow: "none" }}
            >
              <span className="uh-watchlist-more-dropdown-anchor">
                <button
                  type="button"
                  className={
                    moreActive
                      ? "uh-watchlist-group-text-btn uh-watchlist-group-text-btn--on uh-watchlist-more-trigger"
                      : "uh-watchlist-group-text-btn uh-watchlist-more-trigger"
                  }
                >
                  <span className="uh-watchlist-more-label">{moreTriggerLabel}</span>
                  <DownOutlined className="uh-watchlist-more-caret" />
                </button>
              </span>
            </Dropdown>
          </>
        )}
      </div>
      <div
        ref={measureWrapRef}
        className="uh-watchlist-measure-strip"
        aria-hidden="true"
      >
        {groups.map((g, idx) => (
          <button
            key={`mes-${g.id ?? idx}`}
            type="button"
            data-measure-group
            className="uh-watchlist-group-text-btn"
            tabIndex={-1}
          >
            {String(g.groupName ?? "—")}
          </button>
        ))}
        <button
          type="button"
          data-measure-more
          className="uh-watchlist-group-text-btn uh-watchlist-more-trigger"
          tabIndex={-1}
        >
          <span className="uh-watchlist-more-label">{moreTriggerLabel}</span>
          <DownOutlined className="uh-watchlist-more-caret" />
        </button>
      </div>
    </div>
  );
}

interface UserHoldingsPageProps {
  spaceId: string;
}

const REASON_OPTS = ["技术面", "消息面", "基本面"];

function ReasonDetailWithImport(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  maxLength?: number;
  rows?: number;
  placeholder?: string;
  stockCode?: string;
  onImportClick?: () => void;
}): JSX.Element {
  const {
    label,
    value,
    onChange,
    maxLength = 300,
    rows = 4,
    placeholder,
    stockCode,
    onImportClick,
  } = props;
  return (
    <div className="user-holding-form-block" style={{ marginTop: 12 }}>
      <div className="uh-reason-detail-head">
        <span className="user-holding-lab">{label}</span>
        {stockCode && onImportClick ? (
          <button type="button" className="uh-reason-import-btn" onClick={onImportClick}>
            从笔记中导入
          </button>
        ) : null}
      </div>
      <Input.TextArea
        placeholder={placeholder}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="user-holding-input"
      />
    </div>
  );
}

/** 接口日期经 `backend.post` → `convertDates` 后可能为 Date；勿对 Date 做 String 后再按格式解析 */
function formatCnMin(isoLike: unknown): string {
  if (isoLike == null) {
    return "—";
  }
  if (isoLike instanceof Date) {
    const fromDate = dayjs(isoLike);
    return fromDate.isValid() ? fromDate.format("YYYY年MM月DD日 HH:mm") : "—";
  }
  const s = String(isoLike).trim();
  if (!s) {
    return "—";
  }
  const d = dayjs(s, "YYYY-MM-DD HH:mm:ss", true);
  if (d.isValid()) {
    return d.format("YYYY年MM月DD日 HH:mm");
  }
  const loose = dayjs(s);
  if (loose.isValid()) {
    return loose.format("YYYY年MM月DD日 HH:mm");
  }
  return s;
}

/** 接口日期字段经 `convertDates` 可能为 Date 或字符串，便于按日前缀比较 */
function holdingsApiDateTimeString(v: unknown): string {
  if (v == null || v === "") {
    return "";
  }
  if (typeof v === "string") {
    return v.trim();
  }
  if (v instanceof Date) {
    // 与 Backend.convertDates 一致：按本地日历还原，避免 dayjs 默认解析偏移
    const y = v.getFullYear();
    const m = String(v.getMonth() + 1).padStart(2, "0");
    const d = String(v.getDate()).padStart(2, "0");
    const h = String(v.getHours()).padStart(2, "0");
    const min = String(v.getMinutes()).padStart(2, "0");
    const s = String(v.getSeconds()).padStart(2, "0");
    return `${y}-${m}-${d} ${h}:${min}:${s}`;
  }
  return String(v).trim();
}

/** 业务日 yyyy-MM-dd：优先信接口字符串日期前缀（上海业务日），避免 Date/时区踩坑 */
function holdingsBusinessDayKey(v: unknown): string {
  if (v == null || v === "") {
    return "";
  }
  if (typeof v === "string") {
    const t = v.trim();
    const m = t.match(/^(\d{4}-\d{2}-\d{2})/);
    if (!m) {
      return "";
    }
    // 无时区的 "yyyy-MM-dd HH:mm:ss" / "yyyy-MM-dd"：前缀即业务日
    if (!t.includes("T") && !t.endsWith("Z")) {
      return m[1];
    }
    // ISO 带时区：按本地日历（用户在上海）
    const d = dayjs(t);
    return d.isValid() ? d.format("YYYY-MM-DD") : m[1];
  }
  if (v instanceof Date) {
    return holdingsApiDateTimeString(v).slice(0, 10);
  }
  return holdingsApiDateTimeString(v).slice(0, 10);
}

/** 是否与指定统计日同一业务日 */
function holdingsIsSameDay(v: unknown, day: Dayjs): boolean {
  const key = holdingsBusinessDayKey(v);
  return !!key && key === day.format("YYYY-MM-DD");
}

/** 持仓当日浮动盈亏：统计日买入用买入价作基础价；否则按涨跌幅反推昨收 */
function openHoldingTodayFloatProfit(r: AppUserHoldingLineDTO, tradeDay: Dayjs): number {
  const shares = typeof r.buyShares === "number" ? r.buyShares : 0;
  const latest =
    typeof r.latestPrice === "number"
      ? r.latestPrice
      : typeof r.spotPriceSuggest === "number"
        ? r.spotPriceSuggest
        : null;
  if (latest == null || shares <= 0) {
    return 0;
  }
  if (holdingsIsSameDay(r.buyTime, tradeDay)) {
    const buyPx = typeof r.buyPrice === "number" ? r.buyPrice : null;
    if (buyPx == null) {
      return 0;
    }
    return (latest - buyPx) * shares;
  }
  if (typeof r.todayChangePct !== "number") {
    return 0;
  }
  const pct = ratioToStockPricePct(r.todayChangePct);
  const denom = 100 + pct;
  if (!Number.isFinite(pct) || denom === 0) {
    return 0;
  }
  return (latest * shares * pct) / denom;
}

/** 持仓当日浮动盈亏比例（百分比数值）；无法计算时返回 null */
function openHoldingTodayFloatProfitRate(
  r: AppUserHoldingLineDTO,
  tradeDay: Dayjs
): number | null {
  const latest =
    typeof r.latestPrice === "number"
      ? r.latestPrice
      : typeof r.spotPriceSuggest === "number"
        ? r.spotPriceSuggest
        : null;
  if (latest == null) {
    return null;
  }
  if (holdingsIsSameDay(r.buyTime, tradeDay)) {
    const buyPx = typeof r.buyPrice === "number" ? r.buyPrice : null;
    if (buyPx == null || !(buyPx > 0)) {
      return null;
    }
    return ((latest - buyPx) / buyPx) * 100;
  }
  if (typeof r.todayChangePct !== "number") {
    return null;
  }
  const pct = ratioToStockPricePct(r.todayChangePct);
  return Number.isFinite(pct) ? pct : null;
}

/** 持仓当日盈亏基数（统计日买=买入成本；非当日买=昨收×股数） */
function openHoldingTodayFloatBase(r: AppUserHoldingLineDTO, tradeDay: Dayjs): number {
  const shares = typeof r.buyShares === "number" ? r.buyShares : 0;
  if (shares <= 0) {
    return 0;
  }
  if (holdingsIsSameDay(r.buyTime, tradeDay)) {
    return typeof r.buyPrice === "number" ? r.buyPrice * shares : 0;
  }
  const latest =
    typeof r.latestPrice === "number"
      ? r.latestPrice
      : typeof r.spotPriceSuggest === "number"
        ? r.spotPriceSuggest
        : null;
  if (latest == null || typeof r.todayChangePct !== "number") {
    return 0;
  }
  const pct = ratioToStockPricePct(r.todayChangePct);
  const denom = 100 + pct;
  if (!Number.isFinite(pct) || denom === 0) {
    return 0;
  }
  return ((latest * 100) / denom) * shares;
}

/** 统计日卖出对应的「当日赚到的钱」 */
function closedTradeTodayFloatProfit(r: ClosedTradeLineDTO, tradeDay: Dayjs): number {
  const shares = typeof r.sellShares === "number" ? r.sellShares : 0;
  const sellPx = typeof r.sellPrice === "number" ? r.sellPrice : null;
  if (sellPx == null || shares <= 0) {
    return 0;
  }
  if (holdingsIsSameDay(r.buyTime, tradeDay)) {
    const cost = typeof r.costPrice === "number" ? r.costPrice : null;
    if (cost == null) {
      return 0;
    }
    return (sellPx - cost) * shares;
  }
  if (typeof r.todayChangePct !== "number") {
    return 0;
  }
  const pct = ratioToStockPricePct(r.todayChangePct);
  const denom = 100 + pct;
  if (!Number.isFinite(pct) || denom === 0) {
    return 0;
  }
  const latest =
    typeof r.latestPrice === "number" && r.latestPrice > 0 ? r.latestPrice : sellPx;
  const prev = (latest * 100) / denom;
  return (sellPx - prev) * shares;
}

/** 统计日卖出盈亏基数 */
function closedTradeTodayFloatBase(r: ClosedTradeLineDTO, tradeDay: Dayjs): number {
  const shares = typeof r.sellShares === "number" ? r.sellShares : 0;
  if (shares <= 0) {
    return 0;
  }
  if (holdingsIsSameDay(r.buyTime, tradeDay)) {
    return typeof r.costPrice === "number" ? r.costPrice * shares : 0;
  }
  if (typeof r.todayChangePct !== "number") {
    return 0;
  }
  const pct = ratioToStockPricePct(r.todayChangePct);
  const denom = 100 + pct;
  if (!Number.isFinite(pct) || denom === 0) {
    return 0;
  }
  const sellPx = typeof r.sellPrice === "number" ? r.sellPrice : null;
  const latest =
    typeof r.latestPrice === "number" && r.latestPrice > 0
      ? r.latestPrice
      : sellPx;
  if (latest == null) {
    return 0;
  }
  return ((latest * 100) / denom) * shares;
}

type HoldingsSortField =
  | "stock"
  | "latestPrice"
  | "todayProfit"
  | "buyTime"
  | "buyShares"
  | "holdingDays"
  | "positionProfit";
type ClosedSortField =
  | "stock"
  | "todayProfit"
  | "sellShares"
  | "sellPrice"
  | "costPrice"
  | "profitAmount"
  | "profitRate"
  | "sellTime";
type HistorySortField = "stock" | "unitPrice" | "grossAmount" | "bizTime";
type WatchlistSortField = "stock" | "latestPrice" | "fundamental" | "position";
type TableSortOrder = "asc" | "desc";

function HoldingsSortHeader<F extends string>(props: {
  label: string;
  field: F;
  active: boolean;
  order: TableSortOrder;
  onToggle: (field: F) => void;
}): JSX.Element {
  const { label, field, active, order, onToggle } = props;
  const handleToggle = () => onToggle(field);
  return (
    <span className="user-holdings-sort-th">
      <span className="user-holdings-sort-label" onClick={handleToggle}>
        {label}
      </span>
      <button
        type="button"
        className="user-holdings-sort-btn"
        aria-label={`按${label}排序`}
        onClick={handleToggle}
      >
        <CaretUpOutlined
          className={
            active && order === "asc"
              ? "user-holdings-sort-icon user-holdings-sort-icon--active"
              : "user-holdings-sort-icon"
          }
        />
        <CaretDownOutlined
          className={
            active && order === "desc"
              ? "user-holdings-sort-icon user-holdings-sort-icon--active"
              : "user-holdings-sort-icon"
          }
        />
      </button>
    </span>
  );
}

function dateTimeSortMs(v: unknown): number {
  const s = holdingsApiDateTimeString(v);
  if (!s) {
    return Number.NaN;
  }
  const d = dayjs(s, "YYYY-MM-DD HH:mm:ss", true);
  if (d.isValid()) {
    return d.valueOf();
  }
  const loose = dayjs(s);
  return loose.isValid() ? loose.valueOf() : Number.NaN;
}

function compareNullableNumber(a: number | null | undefined, b: number | null | undefined, order: TableSortOrder): number {
  const aOk = typeof a === "number" && Number.isFinite(a);
  const bOk = typeof b === "number" && Number.isFinite(b);
  if (!aOk && !bOk) {
    return 0;
  }
  if (!aOk) {
    return 1;
  }
  if (!bOk) {
    return -1;
  }
  const diff = a - b;
  return order === "asc" ? diff : -diff;
}

function compareStockNameCode(
  aName: unknown,
  aCode: unknown,
  bName: unknown,
  bCode: unknown,
  order: TableSortOrder
): number {
  const na = String(aName ?? "").trim();
  const nb = String(bName ?? "").trim();
  const byName = na.localeCompare(nb, "zh");
  if (byName !== 0) {
    return order === "asc" ? byName : -byName;
  }
  const ca = String(aCode ?? "").trim();
  const cb = String(bCode ?? "").trim();
  const byCode = ca.localeCompare(cb, "en");
  return order === "asc" ? byCode : -byCode;
}

function compareDateTimeField(a: unknown, b: unknown, order: TableSortOrder): number {
  const ta = dateTimeSortMs(a);
  const tb = dateTimeSortMs(b);
  return compareNullableNumber(
    Number.isFinite(ta) ? ta : null,
    Number.isFinite(tb) ? tb : null,
    order
  );
}

function closedProfitRate(r: ClosedTradeLineDTO): number | null {
  const cost = (r.costPrice ?? 0) * (r.sellShares ?? 0);
  if (!(cost > 0) || typeof r.profitAmount !== "number" || !Number.isFinite(r.profitAmount)) {
    return null;
  }
  return (r.profitAmount / cost) * 100;
}

/** 持有天数：买入日到今天（含首尾），无效买入时间返回 null */
function holdingDaysFromBuyTime(buyTime: unknown): number | null {
  const raw = holdingsApiDateTimeString(buyTime);
  const buy = raw ? dayjs(raw) : dayjs(buyTime as string | Date | number);
  if (!buy.isValid()) {
    return null;
  }
  const days = dayjs().startOf("day").diff(buy.startOf("day"), "day") + 1;
  return days > 0 ? days : 1;
}

function sortOpenHoldingsRows(
  rows: AppUserHoldingLineDTO[],
  field: HoldingsSortField | null,
  order: TableSortOrder,
  tradeDay: Dayjs
): AppUserHoldingLineDTO[] {
  if (!field) {
    return rows;
  }
  const list = [...rows];
  list.sort((ra, rb) => {
    switch (field) {
      case "stock":
        return compareStockNameCode(ra.stockName, ra.stockCode, rb.stockName, rb.stockCode, order);
      case "latestPrice":
        return compareNullableNumber(ra.latestPrice, rb.latestPrice, order);
      case "todayProfit":
        return compareNullableNumber(
          openHoldingTodayFloatProfit(ra, tradeDay),
          openHoldingTodayFloatProfit(rb, tradeDay),
          order
        );
      case "buyTime":
        return compareDateTimeField(ra.buyTime, rb.buyTime, order);
      case "buyShares":
        return compareNullableNumber(ra.buyShares, rb.buyShares, order);
      case "holdingDays":
        return compareNullableNumber(
          holdingDaysFromBuyTime(ra.buyTime),
          holdingDaysFromBuyTime(rb.buyTime),
          order
        );
      case "positionProfit":
        return compareNullableNumber(ra.positionProfitAmount, rb.positionProfitAmount, order);
      default:
        return 0;
    }
  });
  return list;
}

function sortClosedTradeRows(
  rows: ClosedTradeLineDTO[],
  field: ClosedSortField | null,
  order: TableSortOrder,
  tradeDay: Dayjs
): ClosedTradeLineDTO[] {
  if (!field) {
    return rows;
  }
  const list = [...rows];
  list.sort((ra, rb) => {
    switch (field) {
      case "stock":
        return compareStockNameCode(ra.stockName, ra.stockCode, rb.stockName, rb.stockCode, order);
      case "todayProfit":
        return compareNullableNumber(
          holdingsIsSameDay(ra.sellTime, tradeDay) ? closedTradeTodayFloatProfit(ra, tradeDay) : null,
          holdingsIsSameDay(rb.sellTime, tradeDay) ? closedTradeTodayFloatProfit(rb, tradeDay) : null,
          order
        );
      case "sellShares":
        return compareNullableNumber(ra.sellShares, rb.sellShares, order);
      case "sellPrice":
        return compareNullableNumber(ra.sellPrice, rb.sellPrice, order);
      case "costPrice":
        return compareNullableNumber(ra.costPrice, rb.costPrice, order);
      case "profitAmount":
        return compareNullableNumber(ra.profitAmount, rb.profitAmount, order);
      case "profitRate":
        return compareNullableNumber(closedProfitRate(ra), closedProfitRate(rb), order);
      case "sellTime":
        return compareDateTimeField(ra.sellTime, rb.sellTime, order);
      default:
        return 0;
    }
  });
  return list;
}

function sortHistoryRows(
  rows: AppUserTradeHistoryLineDTO[],
  field: HistorySortField | null,
  order: TableSortOrder
): AppUserTradeHistoryLineDTO[] {
  if (!field) {
    return rows;
  }
  const list = [...rows];
  list.sort((ra, rb) => {
    switch (field) {
      case "stock":
        return compareStockNameCode(ra.stockName, ra.stockCode, rb.stockName, rb.stockCode, order);
      case "unitPrice":
        return compareNullableNumber(ra.unitPrice, rb.unitPrice, order);
      case "grossAmount":
        return compareNullableNumber(ra.grossAmount, rb.grossAmount, order);
      case "bizTime":
        return compareDateTimeField(ra.bizTime, rb.bizTime, order);
      default:
        return 0;
    }
  });
  return list;
}

function sortWatchlistRows(
  rows: UserWatchlistItemDTO[],
  field: WatchlistSortField | null,
  order: TableSortOrder,
  detailByCode: Map<string, StockDetailData | null>
): UserWatchlistItemDTO[] {
  if (!field) {
    return rows;
  }
  const detailOf = (r: UserWatchlistItemDTO) => detailByCode.get(String(r.stockCode ?? "").trim());
  const list = [...rows];
  list.sort((ra, rb) => {
    switch (field) {
      case "stock":
        return compareStockNameCode(ra.stockName, ra.stockCode, rb.stockName, rb.stockCode, order);
      case "latestPrice":
        return compareNullableNumber(detailOf(ra)?.latestPrice, detailOf(rb)?.latestPrice, order);
      case "fundamental":
        return compareNullableNumber(detailOf(ra)?.performanceScore, detailOf(rb)?.performanceScore, order);
      case "position":
        return compareNullableNumber(detailOf(ra)?.positionScore, detailOf(rb)?.positionScore, order);
      default:
        return 0;
    }
  });
  return list;
}

/** 与 Mock / 领域约定一致：`类型 : 具体原因`（买入理由、交易操作理由） */
function parseBuyReasonDisplay(s: string): { type: string; detail: string } {
  const trySplit = (sep: string): { type: string; detail: string } | null => {
    const i = s.indexOf(sep);
    if (i < 0) {
      return null;
    }
    const type = s.slice(0, i).trim();
    const detail = s.slice(i + sep.length).trim();
    if (type && detail) {
      return { type, detail };
    }
    return null;
  };
  const order = [" : ", "：", ": "] as const;
  for (const sep of order) {
    const r = trySplit(sep);
    if (r) {
      return r;
    }
  }
  const j = s.indexOf(":");
  if (j >= 0) {
    const type = s.slice(0, j).trim();
    const detail = s.slice(j + 1).trim();
    if (type && detail) {
      return { type, detail };
    }
  }
  return { type: "", detail: s };
}

const stockDetailPromiseCache = new Map<string, Promise<StockDetailData | null>>();
const stockDetailValueCache = new Map<string, StockDetailData | null>();

function ensureStockDetailCached(code: string): Promise<StockDetailData | null> {
  const trimmed = code.trim();
  if (!trimmed) {
    return Promise.resolve(null);
  }
  let p = stockDetailPromiseCache.get(trimmed);
  if (!p) {
    p = queryStockDetailInfoForLongfans(trimmed);
    stockDetailPromiseCache.set(trimmed, p);
  }
  return p.then((x) => {
    stockDetailValueCache.set(trimmed, x ?? null);
    return x ?? null;
  });
}

function useStockDetailCached(code: string): StockDetailData | null | undefined {
  const trimmed = code.trim();
  const [d, setD] = useState<StockDetailData | null | undefined>(() =>
    trimmed && stockDetailValueCache.has(trimmed) ? stockDetailValueCache.get(trimmed) : undefined
  );
  useEffect(() => {
    if (!trimmed) {
      setD(null);
      return;
    }
    if (stockDetailValueCache.has(trimmed)) {
      setD(stockDetailValueCache.get(trimmed));
      return;
    }
    void ensureStockDetailCached(trimmed).then((x) => setD(x));
  }, [trimmed]);
  return d;
}

function WatchlistFundamentalScoreCell(props: { stockCode?: string }): JSX.Element {
  const d = useStockDetailCached(String(props.stockCode ?? ""));
  if (d === undefined) {
    return <span className="user-holding-sub">加载中…</span>;
  }
  if (!d) {
    return <span className="user-holding-sub">—</span>;
  }
  const v = d.performanceScore;
  return <span>{typeof v === "number" && Number.isFinite(v) ? v.toFixed(1) : "—"}</span>;
}

function WatchlistPositionScoreCell(props: { stockCode?: string }): JSX.Element {
  const d = useStockDetailCached(String(props.stockCode ?? ""));
  if (d === undefined) {
    return <span className="user-holding-sub">加载中…</span>;
  }
  if (!d) {
    return <span className="user-holding-sub">—</span>;
  }
  const v = d.positionScore;
  return <span>{typeof v === "number" && Number.isFinite(v) ? v.toFixed(1) : "—"}</span>;
}

function WatchlistRowPriceCell(props: { stockCode: string }): JSX.Element {
  const d = useStockDetailCached(props.stockCode);
  if (d === undefined) {
    return <span className="user-holding-sub">加载中…</span>;
  }
  if (!d) {
    return <span className="user-holding-sub">—</span>;
  }
  return (
    <StockPrice
      mode="one-line"
      price={d.latestPrice}
      change={ratioToStockPricePct(d.priceChange)}
    />
  );
}

/** 理由展示：类型为浅蓝标签 + 详情单行省略，点击 Tooltip 看全文（买入理由、操作理由同格式） */
function BuyReasonCell({ text }: { text?: string | null }): JSX.Element {
  const raw = text != null && String(text).trim() !== "" ? String(text).trim() : "";
  if (!raw) {
    return <span className="user-holding-sub">—</span>;
  }
  const { type, detail } = parseBuyReasonDisplay(raw);
  const body = (
    <div className="user-holding-reason-detail-wrap">
      <Tooltip
        title={<div className="user-holding-reason-tooltip-body">{raw}</div>}
        trigger={["click"]}
        overlayInnerStyle={{ maxWidth: 420 }}
      >
        <span className="user-holding-reason-ellipsis">{detail}</span>
      </Tooltip>
    </div>
  );
  return (
    <div className="user-holding-buy-reason-row">
      {type ? <Tag className="user-holding-reason-type-tag">{type}</Tag> : null}
      {body}
    </div>
  );
}

export default function UserHoldingsPage(props: UserHoldingsPageProps): JSX.Element {
  const { spaceId } = props;
  /** 当前激活的 Tab：holdings | closed | history | watchlist */
  const [activeTab, setActiveTab] = useState<"holdings" | "closed" | "history" | "watchlist">(
    "holdings"
  );
  /** listHoldings 仅返回持仓中（OPEN） */
  const [allHoldings, setAllHoldings] = useState<AppUserHoldingLineDTO[]>([]);
  /** 日结库统计日（已完结交易日，与 JOB 一致） */
  const [profitStatDate, setProfitStatDate] = useState<string | null>(null);
  /** 建仓弹窗建议买入时间 */
  const [suggestedBuyTime, setSuggestedBuyTime] = useState<string | null>(null);
  const [suggestedSellTime, setSuggestedSellTime] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshingTodayProfit, setRefreshingTodayProfit] = useState(false);
  const [curve, setCurve] = useState<ProfitCurvePointDTO[]>([]);
  const [hist, setHist] = useState<AppUserTradeHistoryLineDTO[]>([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histPage, setHistPage] = useState(1);

  // 已完结列表数据（SELL 成交流水，近 180 天）
  const [closedTrades, setClosedTrades] = useState<ClosedTradeLineDTO[]>([]);
  const [closedLoading, setClosedLoading] = useState(false);
  const [closedPage, setClosedPage] = useState(1);

  // 前端分页
  const [holdingsPage, setHoldingsPage] = useState(1);
  const HOLDINGS_PAGE_SIZE = 10;
  const [holdingsSortField, setHoldingsSortField] = useState<HoldingsSortField | null>(null);
  const [holdingsSortOrder, setHoldingsSortOrder] = useState<TableSortOrder>("desc");
  const [closedSortField, setClosedSortField] = useState<ClosedSortField | null>(null);
  const [closedSortOrder, setClosedSortOrder] = useState<TableSortOrder>("desc");
  const [historySortField, setHistorySortField] = useState<HistorySortField | null>(null);
  const [historySortOrder, setHistorySortOrder] = useState<TableSortOrder>("desc");
  const [watchSortField, setWatchSortField] = useState<WatchlistSortField | null>(null);
  const [watchSortOrder, setWatchSortOrder] = useState<TableSortOrder>("desc");
  const [watchDetailEpoch, setWatchDetailEpoch] = useState(0);

  // 收益曲线弹框
  const [dailyProfitModalOpen, setDailyProfitModalOpen] = useState(false);
  const [closedProfitModalOpen, setClosedProfitModalOpen] = useState(false);

  const [openBuyModal, setOpenBuyModal] = useState(false);
  const [openBuySeed, setOpenBuySeed] = useState<StockSearchBrief | null>(null);
  const [openBuySeedPrice, setOpenBuySeedPrice] = useState<number | null>(null);
  const [openBuySeedChangePct, setOpenBuySeedChangePct] = useState<number | null>(null);
  const [openBuyForceLotTip, setOpenBuyForceLotTip] = useState(false);
  const [sellModal, setSellModal] = useState<AppUserHoldingLineDTO | null>(null);
  const [editModal, setEditModal] = useState<AppUserHoldingLineDTO | null>(null);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [resetHoldingsOpen, setResetHoldingsOpen] = useState(false);

  const [watchGroups, setWatchGroups] = useState<UserWatchlistGroupDTO[]>([]);
  const [watchGroupId, setWatchGroupId] = useState<string>("");
  const [watchItems, setWatchItems] = useState<UserWatchlistItemDTO[]>([]);
  const [watchLoading, setWatchLoading] = useState(false);
  const [addWatchOpen, setAddWatchOpen] = useState(false);
  const [watchAddGroupOpen, setWatchAddGroupOpen] = useState(false);
  const [watchManageOpen, setWatchManageOpen] = useState(false);
  const {
    stockLinkDetail,
    requestStockDetailForLink,
    buildStockNotesTabProps,
    loadPanel,
    listNotesForImport,
    notesEntitled,
  } = useStockLinkDetail(spaceId);
  const [noteImport, setNoteImport] = useState<{
    stockCode: string;
    stockName: string;
    onPick: (content: string) => void;
  } | null>(null);
  const [writeNoteTarget, setWriteNoteTarget] = useState<{
    code: string;
    name: string;
  } | null>(null);

  const openNoteImport = useCallback(
    (stockCode: string, stockName: string, onPick: (content: string) => void) => {
      if (!notesEntitled) {
        return;
      }
      void loadPanel(stockCode);
      setNoteImport({ stockCode, stockName, onPick });
    },
    [loadPanel, notesEntitled]
  );

  const noteImportHandler = notesEntitled ? openNoteImport : undefined;

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await UserHoldingsApi.listHoldings(spaceId);
      setAllHoldings(data != null && Array.isArray(data.rows) ? data.rows : []);
      const closedStat = String(data?.profitStatDate ?? "").trim();
      setProfitStatDate(closedStat || null);
      const suggested = String(data?.suggestedBuyTime ?? "").trim();
      setSuggestedBuyTime(suggested || null);
      const suggestedSell = String(data?.suggestedSellTime ?? "").trim();
      setSuggestedSellTime(suggestedSell || null);
    } finally {
      setLoading(false);
    }
  }, [spaceId]);

  /** 与 JOB / 刷新接口一致：最近已完结交易日 */
  const profitStatDay = useMemo(() => {
    const parseDay = (raw: unknown): Dayjs | null => {
      const s = holdingsBusinessDayKey(raw) || String(raw ?? "").trim().slice(0, 10);
      const d = dayjs(s, "YYYY-MM-DD", true);
      return d.isValid() ? d.startOf("day") : null;
    };
    const fromClosed = parseDay(profitStatDate);
    if (fromClosed) {
      return fromClosed;
    }
    for (let i = curve.length - 1; i >= 0; i--) {
      const fromCurve = parseDay(curve[i]?.statDate);
      if (fromCurve) {
        return fromCurve;
      }
    }
    return dayjs().subtract(1, "day").startOf("day");
  }, [profitStatDate, curve]);

  // 加载已完结列表
  const loadClosedTrades = useCallback(async () => {
    setClosedLoading(true);
    try {
      const data = await UserHoldingsApi.listClosedTrades(spaceId);
      setClosedTrades(data != null && Array.isArray(data.rows) ? data.rows : []);
    } finally {
      setClosedLoading(false);
    }
  }, [spaceId]);

  /** 我的持仓 Tab 仅展示持仓中 */
  const openHoldingsRows = useMemo(
    () => allHoldings.filter((r) => r.holdingStatus === "OPEN"),
    [allHoldings]
  );

  const heldStockCodes = useMemo(() => {
    const codes: string[] = [];
    openHoldingsRows.forEach((r) => {
      const c = String(r.stockCode ?? "").trim();
      if (c) codes.push(c);
    });
    return codes;
  }, [openHoldingsRows]);

  const sortedOpenHoldingsRows = useMemo(
    () =>
      sortOpenHoldingsRows(openHoldingsRows, holdingsSortField, holdingsSortOrder, profitStatDay),
    [openHoldingsRows, holdingsSortField, holdingsSortOrder, profitStatDay]
  );

  const sortedClosedTrades = useMemo(
    () => sortClosedTradeRows(closedTrades, closedSortField, closedSortOrder, profitStatDay),
    [closedTrades, closedSortField, closedSortOrder, profitStatDay]
  );

  const sortedHistRows = useMemo(
    () => sortHistoryRows(hist ?? [], historySortField, historySortOrder),
    [hist, historySortField, historySortOrder]
  );

  const watchDetailByCode = useMemo(() => {
    void watchDetailEpoch;
    const m = new Map<string, StockDetailData | null>();
    for (const r of watchItems) {
      const code = String(r.stockCode ?? "").trim();
      if (code && stockDetailValueCache.has(code)) {
        m.set(code, stockDetailValueCache.get(code) ?? null);
      }
    }
    return m;
  }, [watchItems, watchDetailEpoch]);

  const sortedWatchItems = useMemo(
    () => sortWatchlistRows(watchItems, watchSortField, watchSortOrder, watchDetailByCode),
    [watchItems, watchSortField, watchSortOrder, watchDetailByCode]
  );

  const handleHoldingsSortToggle = useCallback(
    (field: HoldingsSortField) => {
      setHoldingsPage(1);
      if (holdingsSortField === field) {
        setHoldingsSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
      } else {
        setHoldingsSortField(field);
        setHoldingsSortOrder("desc");
      }
    },
    [holdingsSortField]
  );

  const handleClosedSortToggle = useCallback(
    (field: ClosedSortField) => {
      setClosedPage(1);
      if (closedSortField === field) {
        setClosedSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
      } else {
        setClosedSortField(field);
        setClosedSortOrder("desc");
      }
    },
    [closedSortField]
  );

  const handleHistorySortToggle = useCallback(
    (field: HistorySortField) => {
      setHistPage(1);
      if (historySortField === field) {
        setHistorySortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
      } else {
        setHistorySortField(field);
        setHistorySortOrder("desc");
      }
    },
    [historySortField]
  );

  const handleWatchSortToggle = useCallback(
    (field: WatchlistSortField) => {
      const apply = () => {
        if (watchSortField === field) {
          setWatchSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
        } else {
          setWatchSortField(field);
          setWatchSortOrder("desc");
        }
      };
      if (field === "stock") {
        apply();
        return;
      }
      const codes = watchItems
        .map((r) => String(r.stockCode ?? "").trim())
        .filter((c) => c.length > 0);
      void Promise.all(codes.map((c) => ensureStockDetailCached(c))).then(() => {
        setWatchDetailEpoch((n) => n + 1);
        apply();
      });
    },
    [watchSortField, watchItems]
  );

  const holdingsSortTitle = useCallback(
    (label: string, field: HoldingsSortField) => (
      <HoldingsSortHeader
        label={label}
        field={field}
        active={holdingsSortField === field}
        order={holdingsSortOrder}
        onToggle={handleHoldingsSortToggle}
      />
    ),
    [holdingsSortField, holdingsSortOrder, handleHoldingsSortToggle]
  );

  const closedSortTitle = useCallback(
    (label: string, field: ClosedSortField) => (
      <HoldingsSortHeader
        label={label}
        field={field}
        active={closedSortField === field}
        order={closedSortOrder}
        onToggle={handleClosedSortToggle}
      />
    ),
    [closedSortField, closedSortOrder, handleClosedSortToggle]
  );

  const historySortTitle = useCallback(
    (label: string, field: HistorySortField) => (
      <HoldingsSortHeader
        label={label}
        field={field}
        active={historySortField === field}
        order={historySortOrder}
        onToggle={handleHistorySortToggle}
      />
    ),
    [historySortField, historySortOrder, handleHistorySortToggle]
  );

  const watchSortTitle = useCallback(
    (label: string, field: WatchlistSortField) => (
      <HoldingsSortHeader
        label={label}
        field={field}
        active={watchSortField === field}
        order={watchSortOrder}
        onToggle={handleWatchSortToggle}
      />
    ),
    [watchSortField, watchSortOrder, handleWatchSortToggle]
  );

  const loadCharts = useCallback(async () => {
    const c = await UserHoldingsApi.listProfitCurve(spaceId);
    setCurve(c?.points ?? []);
  }, [spaceId]);

  const loadHist = useCallback(async () => {
    setHistLoading(true);
    try {
      const h = await UserHoldingsApi.listTradeHistory(spaceId);
      setHist(h?.rows ?? []);
    } finally {
      setHistLoading(false);
    }
  }, [spaceId]);

  const loadWatchGroups = useCallback(async () => {
    const g = await UserWatchlistApi.listGroups(spaceId);
    const rows = g?.rows ?? [];
    setWatchGroups(rows);
    setWatchGroupId((prev) => {
      if (prev === WATCHLIST_GROUP_ALL) {
        return WATCHLIST_GROUP_ALL;
      }
      if (prev && rows.some((x) => String(x.id ?? "") === prev)) {
        return prev;
      }
      return WATCHLIST_GROUP_ALL;
    });
  }, [spaceId]);

  const loadWatchItems = useCallback(async () => {
    setWatchLoading(true);
    try {
      const r = await UserWatchlistApi.listItems(spaceId, watchGroupId);
      setWatchItems(r?.rows ?? []);
    } finally {
      setWatchLoading(false);
    }
  }, [spaceId, watchGroupId]);

  /** 交易成功后刷新持仓、已完结、历史与曲线（完结收益依赖 closedTrades） */
  const refreshAfterTrade = useCallback(() => {
    void loadList();
    void loadClosedTrades();
    void loadHist();
    void loadCharts();
  }, [loadList, loadClosedTrades, loadHist, loadCharts]);

  useEffect(() => {
    void loadList();
    void loadClosedTrades();
  }, [loadList, loadClosedTrades]);

  useEffect(() => {
    void loadCharts();
    void loadHist();
  }, [loadCharts, loadHist]);

  // Tab 切换时加载对应数据
  useEffect(() => {
    if (activeTab === "closed") {
      void loadClosedTrades();
    } else if (activeTab === "history") {
      void loadHist();
    } else if (activeTab === "watchlist") {
      void loadWatchGroups();
    }
  }, [activeTab, loadClosedTrades, loadHist, loadWatchGroups]);

  useEffect(() => {
    if (activeTab !== "watchlist") {
      return;
    }
    void loadWatchItems();
  }, [activeTab, loadWatchItems, watchGroupId]);

  const columns: ColumnsType<AppUserHoldingLineDTO> = useMemo(
    () => [
      {
        title: holdingsSortTitle("股票", "stock"),
        align: "left",
        width: 180,
        render: (_: unknown, r) => {
          const code = String(r.stockCode ?? "").trim();
          const name = String(r.stockName ?? "—");
          if (!code) {
            return <span className="user-holding-tight">{name}</span>;
          }
          return (
            <span onClick={(e) => e.stopPropagation()}>
              <StockLink
                stockCode={code}
                stockName={name}
                stockDetail={stockLinkDetail.code === code ? stockLinkDetail.data : null}
                detailLoading={stockLinkDetail.code === code && stockLinkDetail.loading}
                {...stockLinkDetailPropsForCode(stockLinkDetail, code)}
                onRequestDetail={requestStockDetailForLink}
                stockNotesTab={buildStockNotesTabProps(code, name)}
              />
            </span>
          );
        },
      },
      {
        title: holdingsSortTitle("最新价格", "latestPrice"),
        align: "center",
        width: 120,
        render: (_: unknown, r) =>
          typeof r.latestPrice === "number" && typeof r.todayChangePct === "number" ? (
            <StockPrice
              mode="two-line"
              price={r.latestPrice}
              change={ratioToStockPricePct(r.todayChangePct)}
            />
          ) : (
            <span className="user-holding-sub">—</span>
          ),
      },
      {
        title: holdingsSortTitle("今日盈利", "todayProfit"),
        align: "center",
        width: 120,
        render: (_: unknown, r) => {
          const rate = openHoldingTodayFloatProfitRate(r, profitStatDay);
          if (rate == null) {
            return <span className="user-holding-sub">—</span>;
          }
          return (
            <StockProfitRate
              value={openHoldingTodayFloatProfit(r, profitStatDay)}
              rate={rate}
              lines={2}
            />
          );
        },
      },
      {
        title: holdingsSortTitle("买入", "buyTime"),
        align: "center",
        width: 168,
        render: (_: unknown, r) => (
          <div>
            <Amount value={r.buyPrice} />
            <div className="user-holding-sub">{formatCnMin(r.buyTime)}</div>
          </div>
        ),
      },
      {
        title: holdingsSortTitle("买入股数", "buyShares"),
        align: "center",
        width: 88,
        dataIndex: "buyShares",
      },
      {
        title: "买入理由",
        align: "left",
        width: 220,
        onCell: () => ({
          className: "user-holding-reason-td",
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
        }),
        render: (_: unknown, r) => <BuyReasonCell text={r.buyReasonDisplay} />,
      },
      {
        title: holdingsSortTitle("持有天数", "holdingDays"),
        align: "center",
        width: 100,
        render: (_: unknown, r) => {
          const days = holdingDaysFromBuyTime(r.buyTime);
          return days == null ? (
            <span className="user-holding-sub">—</span>
          ) : (
            <span>{days}天</span>
          );
        },
      },
      {
        title: holdingsSortTitle("持仓收益", "positionProfit"),
        align: "center",
        width: 120,
        render: (_: unknown, r) => (
          <StockProfitRate
            value={r.positionProfitAmount ?? null}
            rate={
              typeof r.positionProfitRatio === "number" && Number.isFinite(r.positionProfitRatio)
                ? r.positionProfitRatio
                : null
            }
            lines={2}
          />
        ),
      },
     
      {
        title: "操作",
        align: "center",
        fixed: "right",
        width: 140,
        render: (_: unknown, r) => (
          <span onClick={(e) => e.stopPropagation()}>
            <Button type="link" size="small" disabled={r.holdingStatus !== "OPEN"} onClick={() => setSellModal(r)}>
              卖出
            </Button>
            <Button
              type="link"
              size="small"
              disabled={r.holdingStatus !== "OPEN"}
              onClick={() => {
                const code = String(r.stockCode ?? "").trim();
                if (!code) return;
                setOpenBuySeed({
                  id: 0,
                  code,
                  name: String(r.stockName ?? "").trim() || code,
                });
                setOpenBuySeedPrice(
                  typeof r.latestPrice === "number" && r.latestPrice > 0
                    ? r.latestPrice
                    : typeof r.spotPriceSuggest === "number" && r.spotPriceSuggest > 0
                      ? r.spotPriceSuggest
                      : null
                );
                setOpenBuySeedChangePct(
                  typeof r.todayChangePct === "number" && Number.isFinite(r.todayChangePct)
                    ? r.todayChangePct
                    : null
                );
                setOpenBuyForceLotTip(true);
                setOpenBuyModal(true);
              }}
            >
              加仓
            </Button>
          </span>
        ),
      },
    ],
    [
      stockLinkDetail,
      requestStockDetailForLink,
      buildStockNotesTabProps,
      holdingsSortTitle,
      profitStatDay,
    ]
  );

  // 今日收益：统计日与 JOB 一致；若该日已有日结则优先用库，否则前端按同口径估算
  const todayProfit = useMemo(() => {
    const todayStr = profitStatDay.format("YYYY-MM-DD");
    const todayPoint = curve.find(
      (p) => holdingsApiDateTimeString(p.statDate).slice(0, 10) === todayStr
    );
    if (
      todayPoint &&
      typeof todayPoint.dailyProfitAmount === "number" &&
      Number.isFinite(todayPoint.dailyProfitAmount)
    ) {
      return {
        amount: todayPoint.dailyProfitAmount,
        rate:
          typeof todayPoint.dailyProfitRate === "number" &&
          Number.isFinite(todayPoint.dailyProfitRate)
            ? todayPoint.dailyProfitRate
            : 0,
      };
    }

    let profitAmount = 0;
    let costAmount = 0;

    openHoldingsRows.forEach((r) => {
      profitAmount += openHoldingTodayFloatProfit(r, profitStatDay);
      costAmount += openHoldingTodayFloatBase(r, profitStatDay);
    });

    closedTrades.forEach((r) => {
      if (!holdingsIsSameDay(r.sellTime, profitStatDay)) {
        return;
      }
      profitAmount += closedTradeTodayFloatProfit(r, profitStatDay);
      costAmount += closedTradeTodayFloatBase(r, profitStatDay);
    });

    const profitRate = costAmount > 0 ? (profitAmount / costAmount) * 100 : 0;
    return { amount: profitAmount, rate: profitRate };
  }, [curve, openHoldingsRows, closedTrades, profitStatDay]);

  // 完结收益：近 180 天卖出成交流水的盈亏合计（盈亏/成本）
  const closedProfit = useMemo(() => {
    let profitAmount = 0;
    let costAmount = 0;

    closedTrades.forEach((r) => {
      const shares = typeof r.sellShares === "number" ? r.sellShares : 0;
      const costPx = typeof r.costPrice === "number" ? r.costPrice : null;
      if (costPx != null && shares > 0) {
        costAmount += costPx * shares;
      }
      if (typeof r.profitAmount === "number") {
        profitAmount += r.profitAmount;
      } else if (typeof r.sellPrice === "number" && costPx != null && shares > 0) {
        profitAmount += (r.sellPrice - costPx) * shares;
      }
    });

    const profitRate = costAmount > 0 ? Number(((profitAmount / costAmount) * 100).toFixed(2)) : 0;
    return { amount: profitAmount, rate: profitRate };
  }, [closedTrades]);

  /** 完结胜率：近 180 天卖出笔中盈亏≥0 的占比 */
  const closedWinRatePct = useMemo(() => {
    const total = closedTrades.length;
    if (total === 0) {
      return null;
    }
    const isNonLoss = (r: ClosedTradeLineDTO): boolean => {
      if (typeof r.profitAmount === "number" && Number.isFinite(r.profitAmount)) {
        return r.profitAmount >= 0;
      }
      const shares = typeof r.sellShares === "number" ? r.sellShares : 0;
      const costPx = typeof r.costPrice === "number" ? r.costPrice : null;
      const sellPx = typeof r.sellPrice === "number" ? r.sellPrice : null;
      if (costPx != null && sellPx != null && shares > 0) {
        return (sellPx - costPx) * shares >= 0;
      }
      return false;
    };
    const wins = closedTrades.filter(isNonLoss).length;
    return Number(((wins / total) * 100).toFixed(2));
  }, [closedTrades]);

  // 持仓列表前端分页（先全量排序再切片）
  const holdingsPageSlice = useMemo(() => {
    return sortedOpenHoldingsRows.slice(
      (holdingsPage - 1) * HOLDINGS_PAGE_SIZE,
      holdingsPage * HOLDINGS_PAGE_SIZE
    );
  }, [sortedOpenHoldingsRows, holdingsPage]);

  const reloadCurrentTabData = useCallback(() => {
    if (activeTab === "holdings") {
      void loadList();
      void loadClosedTrades();
      void loadCharts();
    } else if (activeTab === "closed") {
      void loadClosedTrades();
    } else if (activeTab === "watchlist") {
      void loadWatchGroups();
      void loadWatchItems();
    } else {
      void loadHist();
    }
  }, [
    activeTab,
    loadList,
    loadClosedTrades,
    loadCharts,
    loadHist,
    loadWatchGroups,
    loadWatchItems,
  ]);

  const openProfitCurveModal = useCallback(
    async (kind: "daily" | "closed") => {
      if (kind === "daily") {
        setDailyProfitModalOpen(true);
      } else {
        setClosedProfitModalOpen(true);
      }
      if (curve.length === 0) {
        await UserHoldingsApi.refreshStat(spaceId);
        await loadCharts();
      }
    },
    [curve.length, spaceId, loadCharts]
  );

  const refreshTodayProfit = useCallback(async () => {
    if (refreshingTodayProfit) return;
    setRefreshingTodayProfit(true);
    try {
      await UserHoldingsApi.refreshStat(spaceId);
      await Promise.all([loadCharts(), loadClosedTrades()]);
      message.success("今日收益已重新计算");
    } catch {
      message.error("重新计算失败");
    } finally {
      setRefreshingTodayProfit(false);
    }
  }, [refreshingTodayProfit, spaceId, loadCharts, loadClosedTrades]);

  const tabBarMetricsExtra = useMemo(() => {
    const refreshSpin =
      activeTab === "holdings"
        ? loading
        : activeTab === "closed"
          ? closedLoading
          : activeTab === "history"
            ? histLoading
            : watchLoading;
    return (
      <div className="user-holdings-tab-bar-extra-right">
        <div className="user-holdings-profit-metrics-row">
          <div
            className="user-holdings-profit-item"
            onClick={() => void openProfitCurveModal("daily")}
          >
            <span className="user-holdings-profit-label">今日收益</span>
            <StockProfitRate value={todayProfit.amount} rate={todayProfit.rate} lines={1} />
            <Tooltip title="重新计算今日收益">
              <span
                className="user-holdings-today-profit-refresh"
                onClick={(e) => {
                  e.stopPropagation();
                  void refreshTodayProfit();
                }}
              >
                <ReloadOutlined spin={refreshingTodayProfit} />
              </span>
            </Tooltip>
          </div>
          <div
            className="user-holdings-profit-item"
            onClick={() => void openProfitCurveModal("closed")}
          >
            <span className="user-holdings-profit-label">完结收益</span>
            <StockProfitRate value={closedProfit.amount} rate={closedProfit.rate} lines={1} />
            <Tooltip title="仅统计最近180天内的完结股票">
              <span className="user-holding-hint-trigger" onClick={(e) => e.stopPropagation()}>
                <QuestionCircleOutlined />
              </span>
            </Tooltip>
          </div>
          <div className="user-holdings-profit-item user-holdings-profit-item--readonly">
            <span className="user-holdings-profit-label">完结胜率</span>
            <span className="user-holdings-win-rate-value">
              {closedWinRatePct == null ? "—" : `${closedWinRatePct.toFixed(2)}%`}
            </span>
            <Tooltip title="仅统计最近180天内的完结股票">
              <span className="user-holding-hint-trigger">
                <QuestionCircleOutlined />
              </span>
            </Tooltip>
          </div>
        </div>
        <Tooltip title="重新加载数据">
          <span
            className="user-holdings-data-refresh user-holdings-data-refresh--nav-extra"
            role="button"
            tabIndex={0}
            onClick={() => reloadCurrentTabData()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                reloadCurrentTabData();
              }
            }}
          >
            <ReloadOutlined spin={refreshSpin} className="user-holdings-data-refresh-icon" />
            <span className="user-holdings-data-refresh-text">更新数据</span>
          </span>
        </Tooltip>
      </div>
    );
  }, [
    activeTab,
    loading,
    closedLoading,
    histLoading,
    watchLoading,
    reloadCurrentTabData,
    todayProfit,
    refreshTodayProfit,
    refreshingTodayProfit,
    closedProfit,
    closedWinRatePct,
    openProfitCurveModal,
  ]);

  // 转换曲线数据为CanvasCurveChart格式（statDate 统一成 YYYY-MM-DD 字符串）
  const dailyProfitCurveData: CurveDataPoint[] = useMemo(() => {
    return curve.map((p) => ({
      amount: typeof p.dailyProfitAmount === "number" ? p.dailyProfitAmount : 0,
      date: holdingsApiDateTimeString(p.statDate).slice(0, 10),
    }));
  }, [curve]);

  const closedProfitCurveData: CurveDataPoint[] = useMemo(() => {
    return curve.map((p) => ({
      amount: typeof p.closedProfitAmount === "number" ? p.closedProfitAmount : 0,
      date: holdingsApiDateTimeString(p.statDate).slice(0, 10),
    }));
  }, [curve]);

  const pageSizeHist = 20;
  const histPageSlice = sortedHistRows.slice(
    (histPage - 1) * pageSizeHist,
    histPage * pageSizeHist
  );

  const historyCols: ColumnsType<AppUserTradeHistoryLineDTO> = useMemo(
    () => [
      {
        title: historySortTitle("股票", "stock"),
        align: "left",
        width: 100,
        render: (_, r) => {
          const code = String(r.stockCode ?? "").trim();
          const name = String(r.stockName ?? "—");
          if (!code) {
            return <span className="user-holding-tight">{name}</span>;
          }
          return (
            <StockLink
              stockCode={code}
              stockName={name}
              stockDetail={stockLinkDetail.code === code ? stockLinkDetail.data : null}
              detailLoading={stockLinkDetail.code === code && stockLinkDetail.loading}
              {...stockLinkDetailPropsForCode(stockLinkDetail, code)}
              onRequestDetail={requestStockDetailForLink}
              stockNotesTab={buildStockNotesTabProps(code, name)}
            />
          );
        },
      },
      { title: "操作类型", align: "center", dataIndex: "bizTypeDisplay", width: 80 },
      {
        title: historySortTitle("成交价格", "unitPrice"),
        align: "center",
        width: 80,
        render: (_: unknown, r) => <Amount value={r.unitPrice} />,
      },
      {
        title: historySortTitle("总金额/股数", "grossAmount"),
        align: "center",
        width: 80,
        render: (_, r) => (
          <div>
            <Amount value={r.grossAmount} />
            <div className="user-holding-sub">{r.shares}</div>
          </div>
        ),
      },
      {
        title: historySortTitle("交易时间", "bizTime"),
        align: "center",
        dataIndex: "bizTime",
        width: 120,
        render: (t) => formatCnMin(t),
      },
      {
        title: "操作理由",
        align: "left",
        width: 220,
        onCell: () => ({ className: "user-holding-reason-td" }),
        render: (_: unknown, r) => <BuyReasonCell text={r.reasonDisplay} />,
      },
    ],
    [historySortTitle, stockLinkDetail, requestStockDetailForLink, buildStockNotesTabProps]
  );

  // 已完结列表列定义
  const closedTradeColumns: ColumnsType<ClosedTradeLineDTO> = useMemo(
    () => [
      {
        title: closedSortTitle("股票", "stock"),
        align: "left",
        width: 180,
        render: (_: unknown, r) => {
          const code = String(r.stockCode ?? "").trim();
          const name = String(r.stockName ?? "—");
          if (!code) {
            return <span className="user-holding-tight">{name}</span>;
          }
          return (
            <span onClick={(e) => e.stopPropagation()}>
              <StockLink
                stockCode={code}
                stockName={name}
                stockDetail={stockLinkDetail.code === code ? stockLinkDetail.data : null}
                detailLoading={stockLinkDetail.code === code && stockLinkDetail.loading}
                {...stockLinkDetailPropsForCode(stockLinkDetail, code)}
                onRequestDetail={requestStockDetailForLink}
                stockNotesTab={buildStockNotesTabProps(code, name)}
              />
            </span>
          );
        },
      },
      {
        title: closedSortTitle("今日收益", "todayProfit"),
        align: "center",
        width: 120,
        render: (_: unknown, r) => {
          if (!holdingsIsSameDay(r.sellTime, profitStatDay)) {
            return <span className="user-holding-sub">—</span>;
          }
          const amount = closedTradeTodayFloatProfit(r, profitStatDay);
          const base = closedTradeTodayFloatBase(r, profitStatDay);
          const rate = base > 0 ? (amount / base) * 100 : null;
          return <StockProfitRate value={amount} rate={rate} lines={2} />;
        },
      },
      {
        title: closedSortTitle("卖出股数", "sellShares"),
        align: "center",
        width: 80,
        dataIndex: "sellShares",
      },
      {
        title: closedSortTitle("卖出价格", "sellPrice"),
        align: "center",
        width: 80,
        render: (_: unknown, r) => <Amount value={r.sellPrice} />,
      },
      {
        title: closedSortTitle("成本价", "costPrice"),
        align: "center",
        width: 80,
        render: (_: unknown, r) => <Amount value={r.costPrice} />,
      },
      {
        title: closedSortTitle("盈亏金额", "profitAmount"),
        align: "center",
        width: 100,
        render: (_: unknown, r) => {
          const v = r.profitAmount ?? 0;
          return (
            <span className={v >= 0 ? "user-holding-up" : "user-holding-down"}>
              <Amount value={v} />
            </span>
          );
        },
      },
      {
        title: closedSortTitle("盈亏比例", "profitRate"),
        align: "center",
        width: 100,
        render: (_: unknown, r) => {
          const cost = (r.costPrice ?? 0) * (r.sellShares ?? 0);
          const profit = r.profitAmount ?? 0;
          const rate = cost > 0 ? (profit / cost) * 100 : 0;
          return <StockProfitRate value={profit} rate={rate} lines={1} />;
        },
      },
      {
        title: closedSortTitle("卖出时间", "sellTime"),
        align: "center",
        dataIndex: "sellTime",
        width: 140,
        render: (t) => formatCnMin(t),
      },
      {
        title: "卖出理由",
        align: "left",
        width: 220,
        onCell: () => ({ className: "user-holding-reason-td" }),
        render: (_: unknown, r) => <BuyReasonCell text={r.reasonDisplay} />,
      },
    ],
    [
      closedSortTitle,
      stockLinkDetail,
      requestStockDetailForLink,
      buildStockNotesTabProps,
      profitStatDay,
    ]
  );

  const watchlistColumns: ColumnsType<UserWatchlistItemDTO> = useMemo(
    () => [
      {
        title: watchSortTitle("股票", "stock"),
        align: "left",
        width: 168,
        render: (_: unknown, r) => {
          const code = String(r.stockCode ?? "").trim();
          const name = String(r.stockName ?? "—");
          if (!code) {
            return <span className="user-holding-tight">{name}</span>;
          }
          return (
            <StockLink
              stockCode={code}
              stockName={name}
              stockDetail={stockLinkDetail.code === code ? stockLinkDetail.data : null}
              detailLoading={stockLinkDetail.code === code && stockLinkDetail.loading}
              {...stockLinkDetailPropsForCode(stockLinkDetail, code)}
              onRequestDetail={requestStockDetailForLink}
              stockNotesTab={buildStockNotesTabProps(code, name)}
            />
          );
        },
      },
      {
        title: "添加理由",
        align: "left",
        width: 200,
        onCell: () => ({ className: "user-holding-reason-td" }),
        render: (_: unknown, r) => (
          <Tooltip title={String(r.addReason ?? "").trim() || undefined}>
            <span className="user-holding-reason-ellipsis">
              {r.addReason != null && String(r.addReason).trim() !== "" ? String(r.addReason).trim() : "—"}
            </span>
          </Tooltip>
        ),
      },
      {
        title: watchSortTitle("股票价格", "latestPrice"),
        align: "center",
        width: 112,
        render: (_: unknown, r) => <WatchlistRowPriceCell stockCode={String(r.stockCode ?? "")} />,
      },
      {
        title: watchSortTitle("基本面分", "fundamental"),
        align: "center",
        width: 88,
        render: (_: unknown, r) => <WatchlistFundamentalScoreCell stockCode={r.stockCode} />,
      },
      {
        title: watchSortTitle("位置分", "position"),
        align: "center",
        width: 88,
        render: (_: unknown, r) => <WatchlistPositionScoreCell stockCode={r.stockCode} />,
      },
      {
        title: "操作",
        align: "center",
        fixed: "right",
        width: 168,
        render: (_: unknown, r) => (
          <>
            <Button
              type="link"
              size="small"
              onClick={() =>
                void (async () => {
                  const code = String(r.stockCode ?? "").trim();
                  if (!code) {
                    return;
                  }
                  const detail = await ensureStockDetailCached(code);
                  const px = detail?.latestPrice;
                  setOpenBuySeed({ id: 0, code, name: String(r.stockName ?? "").trim() || code });
                  setOpenBuySeedPrice(
                    typeof px === "number" && Number.isFinite(px) && px > 0 ? px : null
                  );
                  setOpenBuyForceLotTip(
                    heldStockCodes.some((c) => c.toUpperCase() === code.toUpperCase())
                  );
                  setOpenBuyModal(true);
                })()
              }
            >
              建仓
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => {
                Modal.confirm({
                  title: "移出自选股",
                  content: `确定从分组「${watchGroups.find((g) => g.id === r.groupId)?.groupName ?? watchGroups.find((g) => g.id === watchGroupId)?.groupName ?? ""}」中移除「${r.stockName ?? r.stockCode}」吗？`,
                  okText: "确认",
                  cancelText: "取消",
                  okButtonProps: { className: RECT_BTN, style: { width: 120 } },
                  cancelButtonProps: { className: RECT_BTN, style: { width: 120 } },
                  onOk: () =>
                    UserWatchlistApi.removeItem(spaceId, String(r.id ?? "")).then((ok) => {
                      if (ok) {
                        message.success("已移除");
                        void loadWatchItems();
                      } else {
                        message.error("移除失败");
                      }
                    }),
                });
              }}
            >
              移除
            </Button>
          </>
        ),
      },
    ],
    [
      stockLinkDetail,
      requestStockDetailForLink,
      buildStockNotesTabProps,
      spaceId,
      loadWatchItems,
      watchGroups,
      watchGroupId,
      watchSortTitle,
      heldStockCodes,
    ]
  );

  return (
    <div className="user-holdings-page" style={userHoldingsPageCssVars()}>
      <Tabs
        className="user-holdings-main-tabs"
        activeKey={activeTab}
        tabBarExtraContent={{ right: tabBarMetricsExtra }}
        onChange={(k) => {
          setActiveTab(k as "holdings" | "closed" | "history" | "watchlist");
          setHoldingsPage(1);
          setClosedPage(1);
          setHistPage(1);
        }}
        items={[
          {
            key: "holdings",
            label: "我的持仓",
            children: (
              <>
                <div className="user-holdings-tab-toolbar">
                  <Button
                    type="primary"
                    className={`${RECT_BTN} user-holdings-open-buy-btn`}
                    style={{ width: 120 }}
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setOpenBuySeed(null);
                      setOpenBuySeedPrice(null);
                      setOpenBuySeedChangePct(null);
                      setOpenBuyForceLotTip(false);
                      setOpenBuyModal(true);
                    }}
                  >
                    建仓买入
                  </Button>
                </div>
                <Table
                  className="user-holdings-table-plain user-holdings-table-clickable-rows"
                  loading={loading}
                  bordered={false}
                  rowKey={(r) => r.holdingId ?? `${r.stockCode}-${r.buyTime}`}
                  dataSource={holdingsPageSlice}
                  pagination={{
                    pageSize: HOLDINGS_PAGE_SIZE,
                    total: sortedOpenHoldingsRows.length,
                    current: holdingsPage,
                    onChange: (p) => setHoldingsPage(p),
                    showSizeChanger: false,
                  }}
                  columns={columns}
                  scroll={{ x: true }}
                  onRow={(r) => ({
                    onClick: () => {
                      if (r.holdingStatus !== "OPEN") {
                        return;
                      }
                      if (!holdingsIsSameDay(r.buyTime, dayjs())) {
                        message.info("仅可修改今日买入的持仓");
                        return;
                      }
                      setEditModal(r);
                    },
                  })}
                />
              </>
            ),
          },
          {
            key: "closed",
            label: "已完结",
            children: (
              <>
                <Table
                  className="user-holdings-table-plain"
                  loading={closedLoading}
                  bordered={false}
                  rowKey={(r) => r.tradeId ?? `${r.stockCode}-${r.sellTime}`}
                  dataSource={sortedClosedTrades.slice((closedPage - 1) * HOLDINGS_PAGE_SIZE, closedPage * HOLDINGS_PAGE_SIZE)}
                  pagination={{
                    pageSize: HOLDINGS_PAGE_SIZE,
                    total: sortedClosedTrades.length,
                    current: closedPage,
                    onChange: (p) => setClosedPage(p),
                    showSizeChanger: false,
                  }}
                  columns={closedTradeColumns}
                  scroll={{ x: true }}
                />
              </>
            ),
          },
          {
            key: "history",
            label: "交易历史",
            children: (
              <>
                <div className="user-holdings-tab-toolbar">
                  <Button
                    type="link"
                    className="user-holdings-reset-holdings-btn"
                    onClick={() => setResetHoldingsOpen(true)}
                  >
                    重置持仓
                  </Button>
                  <Button
                    type="primary"
                    className={RECT_BTN}
                    style={{ width: 120 }}
                    onClick={() => setComingSoonOpen(true)}
                  >
                    月度AI复盘
                  </Button>
                </div>
                <Table
                  className="user-holdings-table-plain"
                  loading={histLoading}
                  bordered={false}
                  columns={historyCols}
                  dataSource={histPageSlice}
                  pagination={{
                    pageSize: pageSizeHist,
                    total: sortedHistRows.length,
                    current: histPage,
                    onChange: (p) => setHistPage(p),
                  }}
                  rowKey={(_, idx) => `h-${idx}`}
                />
              </>
            ),
          },
          {
            key: "watchlist",
            label: "自选股",
            children: (
              <div className="uh-watchlist-layout">
                <div className="uh-watchlist-toolbar-row" aria-label="自选分组与操作">
                  <div className="uh-watchlist-toolbar-groups-grow">
                    <WatchlistGroupsNav
                      groups={watchGroups}
                      watchGroupId={watchGroupId}
                      onSelectGroup={setWatchGroupId}
                    />
                  </div>
                  <div className="uh-watchlist-toolbar-actions">
                    <Button
                      type="link"
                      className="uh-watchlist-toolbar-text-btn"
                      onClick={() => setWatchAddGroupOpen(true)}
                    >
                      添加分组
                    </Button>
                    <Button
                      type="link"
                      className="uh-watchlist-toolbar-text-btn"
                      style={{marginRight: 12}}
                      onClick={() => setWatchManageOpen(true)}
                    >
                      分组管理
                    </Button>
                   
                    <Button
                      type="primary"
                      className={`${RECT_BTN} uh-watchlist-add-stock-btn`}
                      style={{ width: 120 }}
                      icon={<PlusOutlined />}
                      onClick={() => setAddWatchOpen(true)}
                    >
                      添加自选
                    </Button>
                  </div>
                </div>
                <div className="uh-watchlist-main">
                  <Table
                    className="user-holdings-table-plain"
                    loading={watchLoading}
                    bordered={false}
                    rowKey={(r) => r.id ?? `${r.stockCode}-${r.createdAt}`}
                    dataSource={sortedWatchItems}
                    pagination={false}
                    columns={watchlistColumns}
                    scroll={{ x: true }}
                  />
                </div>
              </div>
            ),
          },
        ]}
      />

      <AddWatchlistItemModal
        spaceId={spaceId}
        open={addWatchOpen}
        groups={watchGroups}
        defaultSelectedGroupId={watchGroupId}
        onRefreshGroups={() => void loadWatchGroups()}
        onOpenNoteImport={noteImportHandler}
        onClose={(ok) => {
          setAddWatchOpen(false);
          if (ok) {
            void loadWatchItems();
          }
        }}
      />

      <WatchlistCreateGroupModal
        open={watchAddGroupOpen}
        spaceId={spaceId}
        onClose={() => setWatchAddGroupOpen(false)}
        onAfterListRefresh={() => void loadWatchGroups()}
      />

      <Modal
        title="分组管理"
        open={watchManageOpen}
        onCancel={() => setWatchManageOpen(false)}
        footer={null}
        width={480}
        destroyOnClose
        className="uh-watchlist-manage-modal"
      >
        {watchGroups.length === 0 ? (
          <div className="uh-watchlist-manage-empty">暂无分组</div>
        ) : (
          <ul className="uh-watchlist-manage-list">
            {watchGroups.map((g, idx) => {
              const gid = String(g.id ?? "");
              const gname = String(g.groupName ?? "—");
              return (
                <li key={g.id ?? `mg-${idx}`} className="uh-watchlist-manage-row">
                  <span className="uh-watchlist-manage-name" title={gname}>
                    {gname}
                  </span>
                  <Button
                    type="link"
                    danger
                    className="uh-watchlist-manage-del"
                    onClick={() => {
                      Modal.confirm({
                        title: "删除分组",
                        content: `确定删除分组「${gname}」及其中的自选股票吗？`,
                        okText: "确认",
                        cancelText: "取消",
                        okButtonProps: { className: RECT_BTN, style: { width: 120 } },
                        cancelButtonProps: { className: RECT_BTN, style: { width: 120 } },
                        onOk: () =>
                          UserWatchlistApi.deleteGroup(spaceId, gid).then((ok) => {
                            if (ok) {
                              message.success("已删除分组");
                              void loadWatchGroups();
                            } else {
                              message.error("删除失败");
                            }
                          }),
                      });
                    }}
                  >
                    删除
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </Modal>

      <OpenBuyModal
        spaceId={spaceId}
        open={openBuyModal}
        seedStock={openBuySeed}
        seedBuyPrice={openBuySeedPrice}
        seedChangePct={openBuySeedChangePct}
        suggestedBuyTime={suggestedBuyTime}
        heldStockCodes={heldStockCodes}
        forceLotTip={openBuyForceLotTip}
        onOpenNoteImport={noteImportHandler}
        onClose={(ok) => {
          setOpenBuyModal(false);
          setOpenBuySeed(null);
          setOpenBuySeedPrice(null);
          setOpenBuySeedChangePct(null);
          setOpenBuyForceLotTip(false);
          if (ok) {
            refreshAfterTrade();
          }
        }}
      />
      <Modal
        title="敬请期待"
        open={comingSoonOpen}
        onCancel={() => setComingSoonOpen(false)}
        footer={[
          <Button
            key="ok"
            type="primary"
            className={RECT_BTN}
            style={{ width: 120 }}
            onClick={() => setComingSoonOpen(false)}
          >
            知道了
          </Button>,
        ]}
        centered
        width={420}
      >
        <p style={{ margin: 0, fontWeight: 300, color: "rgba(0,0,0,0.65)" }}>
          功能正在开发中...敬请期待。
        </p>
      </Modal>

      <ResetHoldingsModal
        open={resetHoldingsOpen}
        spaceId={spaceId}
        onClose={(ok) => {
          setResetHoldingsOpen(false);
          if (ok) {
            refreshAfterTrade();
          }
        }}
      />

      <EditHoldingModalWrap
        key={editModal?.holdingId ?? "edit-closed"}
        spaceId={spaceId}
        row={editModal}
        onOpenNoteImport={noteImportHandler}
        onClose={(ok) => {
          setEditModal(null);
          if (ok) refreshAfterTrade();
        }}
      />
      <SellModalWrap
        key={sellModal?.holdingId ?? "sell-closed"}
        spaceId={spaceId}
        row={sellModal}
        suggestedSellTime={suggestedSellTime}
        onOpenNoteImport={noteImportHandler}
        onClose={(ok) => {
          setSellModal(null);
          if (ok) refreshAfterTrade();
        }}
      />

      <StockNoteImportPicker
        open={noteImport != null}
        stockCode={noteImport?.stockCode ?? ""}
        stockName={noteImport?.stockName}
        notes={noteImport ? listNotesForImport(noteImport.stockCode) : []}
        onClose={() => setNoteImport(null)}
        onPick={(note) => noteImport?.onPick(note.content)}
        onGoWriteNote={() => {
          if (!noteImport) {
            return;
          }
          const target = { code: noteImport.stockCode, name: noteImport.stockName };
          setNoteImport(null);
          setWriteNoteTarget(target);
          requestStockDetailForLink(target.code);
        }}
      />

      {writeNoteTarget ? (
        <StockLink
          hideTrigger
          modalOpen
          initialTabKey="notes"
          stockCode={writeNoteTarget.code}
          stockName={writeNoteTarget.name}
          stockDetail={
            stockLinkDetail.code === writeNoteTarget.code ? stockLinkDetail.data : null
          }
          detailLoading={
            stockLinkDetail.code === writeNoteTarget.code && stockLinkDetail.loading
          }
          {...stockLinkDetailPropsForCode(stockLinkDetail, writeNoteTarget.code)}
          onRequestDetail={requestStockDetailForLink}
          stockNotesTab={buildStockNotesTabProps(writeNoteTarget.code, writeNoteTarget.name)}
          onModalOpenChange={(o) => {
            if (!o) {
              setWriteNoteTarget(null);
            }
          }}
        />
      ) : null}

      {/* 今日收益曲线弹框 */}
      <Modal
        open={dailyProfitModalOpen}
        title="日收益变化曲线"
        onCancel={() => setDailyProfitModalOpen(false)}
        destroyOnClose
        footer={[
          <Button key="close" className={RECT_BTN} style={{ width: 120 }} onClick={() => setDailyProfitModalOpen(false)}>
            关闭
          </Button>,
        ]}
        width={860}
      >
        <div style={{ padding: "20px 0" }}>
          {dailyProfitCurveData.length === 0 ? (
            <div style={{ textAlign: "center", color: "rgba(0,0,0,0.45)", padding: "48px 0", fontWeight: 300 }}>
              暂无日收益统计，收盘后自动更新；也可点「更新数据」后重试
            </div>
          ) : (
            <CanvasCurveChart
              key={`daily-${dailyProfitCurveData.length}`}
              data={dailyProfitCurveData}
              width={800}
              height={320}
            />
          )}
        </div>
      </Modal>

      {/* 完结收益曲线弹框 */}
      <Modal
        open={closedProfitModalOpen}
        title="日完结收益变化曲线"
        onCancel={() => setClosedProfitModalOpen(false)}
        destroyOnClose
        footer={[
          <Button key="close" className={RECT_BTN} style={{ width: 120 }} onClick={() => setClosedProfitModalOpen(false)}>
            关闭
          </Button>,
        ]}
        width={860}
      >
        <div style={{ padding: "20px 0" }}>
          {closedProfitCurveData.length === 0 ? (
            <div style={{ textAlign: "center", color: "rgba(0,0,0,0.45)", padding: "48px 0", fontWeight: 300 }}>
              暂无完结收益统计，收盘后自动更新；也可点「更新数据」后重试
            </div>
          ) : (
            <CanvasCurveChart
              key={`closed-${closedProfitCurveData.length}`}
              data={closedProfitCurveData}
              width={800}
              height={320}
            />
          )}
        </div>
      </Modal>
    </div>
  );
}

const RESET_HOLDINGS_CONFIRM_PHRASE = "我已知晓不可恢复，请继续清空";

function ResetHoldingsModal(props: {
  open: boolean;
  spaceId: string;
  onClose: (ok: boolean) => void;
}): JSX.Element {
  const { open, spaceId, onClose } = props;
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setConfirmText("");
      setSubmitting(false);
    }
  }, [open]);

  const canSubmit = confirmText.trim() === RESET_HOLDINGS_CONFIRM_PHRASE;

  const submit = async () => {
    if (!canSubmit || submitting) {
      return;
    }
    setSubmitting(true);
    try {
      const ok = await UserHoldingsApi.resetHoldings(spaceId);
      if (ok) {
        message.success("持仓已重置清空");
        onClose(true);
      } else {
        message.error("重置失败，请稍后重试");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="重置持仓"
      open={open}
      okText="确认"
      cancelText="取消"
      okButtonProps={{
        className: RECT_BTN,
        style: { width: 120 },
        disabled: !canSubmit,
        loading: submitting,
      }}
      cancelButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      width={520}
      centered
      onOk={() => void submit()}
      onCancel={() => onClose(false)}
      destroyOnClose
    >
      <p style={{ margin: "0 0 12px", fontWeight: 300, color: "rgba(0,0,0,0.65)", lineHeight: 1.6 }}>
        重置后所有历史记录均会清空并且不可恢复，请输入「{RESET_HOLDINGS_CONFIRM_PHRASE}」，并点击确认。
      </p>
      <Input
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        placeholder={`请输入：${RESET_HOLDINGS_CONFIRM_PHRASE}`}
        style={{ borderRadius: 4 }}
        allowClear
      />
    </Modal>
  );
}

/** 点击持仓行（仅今日买入）：回填成本/股数/时间/理由并保存修正 */
function EditHoldingModalWrap(props: {
  spaceId: string;
  row: AppUserHoldingLineDTO | null;
  onOpenNoteImport?: (stockCode: string, stockName: string, onPick: (content: string) => void) => void;
  onClose: (ok: boolean) => void;
}): JSX.Element | null {
  const r = props.row;
  const [buyPrice, setBuyPrice] = useState<number>();
  const [buyShares, setBuyShares] = useState<number>();
  const [reasonType, setReasonType] = useState(REASON_OPTS[0]);
  const [reasonDetail, setReasonDetail] = useState("");
  const [buyTime, setBuyTime] = useState<Dayjs | null>(dayjs());
  const [spotPrice, setSpotPrice] = useState<number>();
  const [spotChangePct, setSpotChangePct] = useState<number | null>(null);

  useEffect(() => {
    if (!r?.holdingId) {
      return;
    }
    setBuyPrice(typeof r.buyPrice === "number" && r.buyPrice > 0 ? r.buyPrice : undefined);
    setBuyShares(typeof r.buyShares === "number" && r.buyShares > 0 ? r.buyShares : undefined);
    const parsed = parseBuyReasonDisplay(String(r.buyReasonDisplay ?? ""));
    const typeFromRow = REASON_OPTS.includes(parsed.type as (typeof REASON_OPTS)[number])
      ? parsed.type
      : REASON_OPTS[0];
    setReasonType(typeFromRow);
    setReasonDetail(parsed.detail);
    const t = holdingsApiDateTimeString(r.buyTime);
    const d = t ? dayjs(t, "YYYY-MM-DD HH:mm:ss", true) : null;
    setBuyTime(d != null && d.isValid() ? d : dayjs(r.buyTime));
    setSpotPrice(
      typeof r.latestPrice === "number" && r.latestPrice > 0
        ? r.latestPrice
        : typeof r.spotPriceSuggest === "number" && r.spotPriceSuggest > 0
          ? r.spotPriceSuggest
          : undefined
    );
    setSpotChangePct(
      typeof r.todayChangePct === "number" && Number.isFinite(r.todayChangePct)
        ? r.todayChangePct
        : null
    );
  }, [r?.holdingId, r?.buyPrice, r?.buyShares, r?.buyTime, r?.buyReasonDisplay, r?.latestPrice, r?.spotPriceSuggest, r?.todayChangePct]);

  if (!r || !r.holdingId) {
    return null;
  }

  const applyLatestPrice = async () => {
    const code = String(r.stockCode ?? "").trim();
    if (!code) {
      return;
    }
    const detail = await queryStockDetailInfoForLongfans(code);
    const px = detail?.latestPrice;
    if (typeof px === "number" && Number.isFinite(px) && px > 0) {
      setBuyPrice(px);
      setSpotPrice(px);
      if (typeof detail?.priceChange === "number" && Number.isFinite(detail.priceChange)) {
        setSpotChangePct(detail.priceChange);
      }
      return;
    }
    if (typeof spotPrice === "number" && spotPrice > 0) {
      setBuyPrice(spotPrice);
      return;
    }
    message.warning("暂无最新价");
  };

  const submit = async () => {
    if (!buyShares || !(buyPrice && buyPrice > 0)) {
      message.error("请填写买入价与股数");
      return;
    }
    const ok = await UserHoldingsApi.updateOpenHolding(props.spaceId, {
      holdingId: r.holdingId!,
      buyPrice,
      buyShares,
      buyTime: (buyTime ?? dayjs()).format("YYYY-MM-DD HH:mm:ss"),
      reasonType,
      reasonDetail,
    });
    if (ok) {
      message.success("已保存");
      props.onClose(true);
    } else {
      message.error("保存失败，请稍后重试");
    }
  };

  return (
    <Modal
      title="编辑持仓"
      open
      okText="确认"
      cancelText="取消"
      okButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      cancelButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      onOk={() => void submit()}
      onCancel={() => props.onClose(false)}
      destroyOnClose
    >
      <div className="user-holding-modal-stock-head">
        <span className="user-holding-modal-stock-name">{r.stockName ?? r.stockCode ?? "—"}</span>
        <StockPrice
          mode="one-line"
          price={spotPrice ?? 0}
          change={ratioToStockPricePct(spotChangePct)}
        />
      </div>
      <div className="user-holding-form-block">
        <div className="user-holding-price-lab-row">
          <span className="user-holding-lab">买入价（成本价）</span>
          <button type="button" className="user-holding-use-latest-btn" onClick={() => void applyLatestPrice()}>
            使用最新价
          </button>
        </div>
        <InputNumber
          placeholder="买入价"
          style={{ width: "100%" }}
          value={buyPrice}
          onChange={(v) => setBuyPrice(typeof v === "number" ? v : undefined)}
          min={0}
          step={0.01}
          controls={false}
        />
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <DatePicker
          className="user-holding-input user-holding-datepicker"
          showTime
          format="YYYY-MM-DD HH:mm:ss"
          style={{ width: "100%" }}
          value={buyTime}
          onChange={(d) => setBuyTime(d)}
          placeholder="买入时间"
          allowClear={false}
        />
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <span className="user-holding-lab">买入股数</span>
        <InputNumber
          placeholder="股数"
          style={{ width: "100%" }}
          value={buyShares}
          min={1}
          onChange={(v) => setBuyShares(typeof v === "number" ? v : undefined)}
          controls={false}
        />
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <span className="user-holding-lab">原因类型</span>
        <Select
          placeholder="请选择"
          options={REASON_OPTS.map((x) => ({ label: x, value: x }))}
          value={reasonType}
          onChange={setReasonType}
          style={{ width: "100%" }}
        />
      </div>
      <ReasonDetailWithImport
        label="具体原因（最多300字）"
        placeholder="请输入"
        value={reasonDetail}
        onChange={setReasonDetail}
        stockCode={r.stockCode}
        onImportClick={
          r.stockCode && props.onOpenNoteImport
            ? () =>
                props.onOpenNoteImport?.(String(r.stockCode), String(r.stockName ?? ""), (content) =>
                  setReasonDetail(content.slice(0, 300))
                )
            : undefined
        }
      />
    </Modal>
  );
}

/** 无后端建议时：盘中用当前，否则上一工作日 15:28 */
function fallbackDefaultSellTime(now: Dayjs = dayjs()): string {
  const minuteOfDay = now.hour() * 60 + now.minute();
  const inSession = minuteOfDay >= 9 * 60 + 30 && minuteOfDay <= 15 * 60;
  const weekday = now.day() >= 1 && now.day() <= 5;
  if (weekday && inSession) {
    return now.format("YYYY-MM-DD HH:mm:ss");
  }
  let d = now.startOf("day");
  do {
    d = d.subtract(1, "day");
  } while (d.day() === 0 || d.day() === 6);
  return d.hour(15).minute(28).second(0).millisecond(0).format("YYYY-MM-DD HH:mm:ss");
}

function resolveDefaultSellTime(suggestedSellTime?: string | null): string {
  const suggested = String(suggestedSellTime ?? "").trim();
  if (suggested) {
    const d = dayjs(suggested, "YYYY-MM-DD HH:mm:ss", true);
    if (d.isValid()) {
      return d.format("YYYY-MM-DD HH:mm:ss");
    }
  }
  return fallbackDefaultSellTime();
}

function SellModalWrap(props: {
  spaceId: string;
  row: AppUserHoldingLineDTO | null;
  suggestedSellTime?: string | null;
  onOpenNoteImport?: (stockCode: string, stockName: string, onPick: (content: string) => void) => void;
  onClose: (ok: boolean) => void;
}): JSX.Element | null {
  const r = props.row;
  const [sellPrice, setSellPrice] = useState<number>();
  const [sellShares, setSellShares] = useState<number>(
    r?.buyShares && r.buyShares > 0 ? r.buyShares : 0
  );
  const [sellTime, setSellTime] = useState(() => resolveDefaultSellTime(props.suggestedSellTime));
  const [reasonType, setReasonType] = useState(REASON_OPTS[0]);
  const [reasonDetail, setReasonDetail] = useState("");

  React.useEffect(() => {
    if (!r?.holdingId) {
      return;
    }
    setSellPrice(
      typeof r.spotPriceSuggest === "number" && r.spotPriceSuggest > 0
        ? r.spotPriceSuggest
        : undefined
    );
    setSellShares(r.buyShares != null && r.buyShares > 0 ? r.buyShares : 0);
    setSellTime(resolveDefaultSellTime(props.suggestedSellTime));
    setReasonType(REASON_OPTS[0]);
    setReasonDetail("");
  }, [r?.holdingId, r?.buyShares, r?.spotPriceSuggest, props.suggestedSellTime]);

  if (!r || !r.holdingId) {
    return null;
  }

  const pct =
    typeof r.positionProfitRatio === "number"
      ? r.positionProfitRatio
      : typeof r.buyPrice === "number" &&
          typeof r.spotPriceSuggest === "number" &&
          r.buyPrice > 0
        ? (((r.spotPriceSuggest ?? 0) - r.buyPrice) / r.buyPrice) * 100
        : 0;

  const submitSell = async () => {
    if (!sellShares || !(sellPrice && sellPrice > 0)) return;
    const ok = await UserHoldingsApi.sell(props.spaceId, {
      holdingId: r.holdingId,
      sellPrice,
      sellShares,
      sellTime,
      reasonType,
      reasonDetail,
    });
    if (ok) {
      message.success("已卖出");
      props.onClose(true);
    }
  };

  return (
    <Modal
      title={`卖出 · ${r.stockName ?? ""}`}
      open
      okText="确认"
      cancelText="取消"
      okButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      cancelButtonProps={{ className: RECT_BTN, style: { width: 120 } }}
      width={560}
      onOk={() => void submitSell()}
      onCancel={() => props.onClose(false)}
    >
      <div className="user-holding-sell-summary">
        <span>现价：{(r.spotPriceSuggest ?? r.buyPrice)?.toFixed(2) ?? "—"}</span>
        <span>；</span>
        <span>
          总收益率：
          <span className={pct > 0 ? "stock-profit-up" : pct < 0 ? "stock-profit-down" : "stock-profit-flat"}>
            {`${pct > 0 ? "+" : ""}${pct.toFixed(2)}%`}
          </span>
        </span>
      </div>
      <div style={{ marginBottom: 8 }}>
        买入均价 <Text>{String(r.buyPrice ?? "—")}</Text>
      </div>
      <div className="user-holding-form-block">
        <span className="user-holding-lab">卖出价格</span>
        <InputNumber placeholder="成交价" style={{ width: "100%" }} value={sellPrice} onChange={(v) => setSellPrice(typeof v === "number" ? v : undefined)} controls={false} />
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12, position: "relative" }}>
        <button type="button" className="user-holding-qingcang" onClick={() => setSellShares(r.buyShares ?? sellShares)}>
          清仓
        </button>
        <span className="user-holding-lab">卖出股数</span>
        <InputNumber
          placeholder="股数"
          style={{ width: "100%" }}
          value={sellShares}
          min={1}
          max={r.buyShares}
          onChange={(v) => setSellShares(typeof v === "number" ? v : r.buyShares ?? 1)}
          controls={false}
        />
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <span className="user-holding-lab">卖出时间</span>
        <Input value={sellTime} onChange={(e) => setSellTime(e.target.value)} placeholder="YYYY-MM-DD HH:mm:ss" />
      </div>
      <div className="user-holding-form-block" style={{ marginTop: 12 }}>
        <span className="user-holding-lab">卖出理由类型</span>
        <Select options={REASON_OPTS.map((x) => ({ label: x, value: x }))} value={reasonType} onChange={setReasonType} style={{ width: "100%" }} />
      </div>
      <ReasonDetailWithImport
        label="具体原因"
        value={reasonDetail}
        onChange={setReasonDetail}
        stockCode={r.stockCode}
        onImportClick={
          r.stockCode && props.onOpenNoteImport
            ? () =>
                props.onOpenNoteImport?.(String(r.stockCode), String(r.stockName ?? ""), (content) =>
                  setReasonDetail(content.slice(0, 300))
                )
            : undefined
        }
      />
    </Modal>
  );
}
